// T3-B sponsored motif (PR 4, 2026-05-07):
// sponsors 管理画面 — list + create/edit sheet + soft-delete。
// LandmarkManagement パターン踏襲。
//
// auth: ADMIN only (= backend systemApi.js で enforced)。
//
// Spec: design/sponsored-motif-spec.md §6.1 / §6.2

import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import SponsorFormSheet from './components/SponsorFormSheet';

export default observer(function SponsorManagementPage() {
    const { t } = useTranslation();
    const {
        sponsorStore: { sponsors, total, list: fetchList, remove: removeSponsor },
        modalStore: { showAlertModal }
    } = useStore();

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);

    useEffect(() => {
        fetchList();
    }, []);

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (sponsor: any) => {
        setSheetMode('edit');
        setEditTarget(sponsor);
        setSheetOpen(true);
    };

    const handleDelete = (id: number, name: string) => {
        showAlertModal({
            title: t('words_title.confirm_delete') || '削除確認',
            size: 'sm',
            content: `「${name}」を削除しますか? (論理削除のため復元可能)`,
            onSave: async () => {
                const res = await flowResult(removeSponsor(id));
                if (res?.ok) {
                    toastify('success', t('messages.delete_success') || '削除しました');
                    fetchList();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    return (
        <div className='layout-card'>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>
                        {t('words_title.sponsor_management') || 'スポンサー管理'}
                    </h2>
                    <p className='text-sm text-gray-500 mt-1'>
                        {`登録件数: ${total}`}
                    </p>
                    <p className='text-xs text-gray-500 mt-2 max-w-2xl leading-relaxed'>
                        おみくじの絵柄枠に協賛ブランドの広告を出す機能です。ここで広告主を登録 →「協賛キャンペーン管理」で契約 → 絵柄を登録すると、シェイク時に協賛絵柄が抽選で出ます。※店舗のクーポン運用とは別機能です。
                    </p>
                </div>
                <Button variant='active' onClick={handleOpenCreate}>
                    {t('button.add_new') || '新規追加'}
                </Button>
            </div>

            {sponsors.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>
                        {t('messages.no_data') || '登録されているスポンサーがありません'}
                    </div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>名称</th>
                                    <th className='py-2 px-3'>連絡先メール</th>
                                    <th className='py-2 px-3'>担当者</th>
                                    <th className='py-2 px-3'>業界</th>
                                    <th className='py-2 px-3 w-[80px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sponsors.map(sp => (
                                    <tr key={sp.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                        <td className='py-2 px-3 text-gray-500'>{sp.id}</td>
                                        <td className='py-2 px-3 font-medium'>{sp.name}</td>
                                        <td className='py-2 px-3 text-gray-600'>{sp.contact_email}</td>
                                        <td className='py-2 px-3 text-gray-600'>{sp.contact_name ?? '-'}</td>
                                        <td className='py-2 px-3 text-gray-600'>{sp.industry ?? '-'}</td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button variant='outline' size='sm' onClick={() => handleOpenEdit(sp)}>
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(sp.id, sp.name)}
                                                    className='text-red-600 hover:text-red-800'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <SponsorFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                sponsor={editTarget}
                onSuccess={fetchList}
            />
        </div>
    );
});
